import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ReferralCodeError,
  ReferralCodeRequestParams,
  ReferralCodeState,
} from './referral-code-types';
import { AsyncThunkConfig } from '../../async-thunk-config';
import { verifyReferralCodeFields } from './referral-code-utils';
import apiManager from '../../../api/api-manager';
import { ReferralCodeResponse } from '../../../api/generated/dob';

const initialState: ReferralCodeState = {
  checkReferralCodeRequestStatus: 'idle',
  referralCodeError: undefined,
  workflowName: undefined,
  sessionId: undefined,
};

export const checkReferralCode = createAsyncThunk<
  ReferralCodeResponse,
  ReferralCodeRequestParams,
  AsyncThunkConfig<ReferralCodeError>
>(
  'verify-referral-code',
  async (params: ReferralCodeRequestParams, thunkApi) => {
    try {
      const validationError = verifyReferralCodeFields(params);
      if (validationError) {
        return thunkApi.rejectWithValue(validationError);
      }
      const response = await apiManager.dobCustomerApi.verifyReferralCode({
        referralCode: params.referralCode,
      });

      if (response?.data?.response?.code !== '0')
        return thunkApi.rejectWithValue('INVALID_REFERRAL_CODE');
      return response.data?.body;
    } catch (error) {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

const referralCodeSlice = createSlice({
  name: 'features/referralCode',
  initialState,
  reducers: {
    resetReferralCodeState() {
      return initialState;
    },
    updateReferralCodeState(state, action: PayloadAction<ReferralCodeError>) {
      state.referralCodeError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkReferralCode.pending, (state) => {
      state.checkReferralCodeRequestStatus = 'loading';
    });
    builder.addCase(checkReferralCode.fulfilled, (state, action) => {
      state.checkReferralCodeRequestStatus = 'succeeded';
      state.workflowName = action.payload.workflowName;
      state.sessionId = action.payload.sessionId;
    });
    builder.addCase(checkReferralCode.rejected, (state, action) => {
      state.referralCodeError = action.payload || 'GENERIC_ERROR';
      state.checkReferralCodeRequestStatus = 'failed';
    });
  },
});

export const { resetReferralCodeState, updateReferralCodeState } =
  referralCodeSlice.actions;

export default referralCodeSlice.reducer;
